import React, { useState, useEffect } from 'react';
import { Shield, Plus, Trash2, X, Check, AlertCircle, Loader2, Search, Edit2, Users } from 'lucide-react';
import { logRoleChange } from '../utils/dataLineage';

const StatusBadge = ({ status }) => {
  const getStatusColor = () => {
    const statusLower = (status || '').toLowerCase();
    switch (statusLower) {
      case "active":
        return { bg: 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))', color: 'var(--cares-green)', border: 'var(--cares-green)' };
      case "disabled":
        return { bg: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))', color: 'var(--cares-orange)', border: 'var(--cares-orange)' };
      default:
        return { bg: 'var(--cares-surface-board)', color: 'var(--cares-text-label)', border: 'var(--cares-line-strong)' };
    }
  };

  const colors = getStatusColor();

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      background: colors.bg,
      color: colors.color,
      border: `1px solid ${colors.border}`
    }}>
      {status || 'Unknown'}
    </span>
  );
};

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);

  const [formData, setFormData] = useState({
    roleName: '',
    roleDescription: ''
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const endpoint = `/api/role_management/get_role`;
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error('Failed to fetch roles');
      
      const data = await response.json();

      let rolesArray;
      
      if (Array.isArray(data)) {
        rolesArray = data;
      } else if (data.roles && Array.isArray(data.roles)) {
        rolesArray = data.roles;
      } else if (data.roles && typeof data.roles === 'string') {
        try {
          rolesArray = JSON.parse(data.roles);
        } catch (parseError) {
          throw new Error('Invalid roles data format');
        }
      } else if (typeof data === 'string') {
        try {
          rolesArray = JSON.parse(data);
        } catch (parseError) {
          throw new Error('Invalid response format');
        }
      } else {
        rolesArray = data;
      }

      if (!Array.isArray(rolesArray)) {
        throw new Error('Roles data is not in expected format');
      }

      const formattedRoles = rolesArray.map(role => ({
        id: role.Role?.replace(/\s+/g, '_').toUpperCase() || `ROLE_${Math.random().toString(36).substr(2, 9)}`,
        name: role.Role || 'Unnamed Role',
        description: role['Role Description'] || '',
        createdDate: role['Created date'] || '',
        createdBy: role['Created by'] || 'Unknown',
        status: role.Status || 'active',
        actions: role.Action || ''
      }));

      setRoles(formattedRoles);
    } catch (err) {
      console.error('Error in fetchRoles:', err);
      setError(`Failed to load roles: ${err.message}`);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const endpoint = isEditMode ? `/api/role_management/update_role` : `/api/role_management/add_role`;
      const params = new URLSearchParams({
        role: formData.roleName,
        description: formData.roleDescription
      });

      if (isEditMode && selectedRole) {
        params.append('old_role', selectedRole.name);
      }

      const response = await fetch(`${endpoint}?${params}`, {
        method: 'POST'
      });

      if (!response.ok) throw new Error(isEditMode ? 'Failed to update role' : 'Failed to add new role');

      const result = await response.json();

      // Log changes to data lineage
      if (isEditMode && selectedRole) {
        // Log role name change if it changed
        if (selectedRole.name !== formData.roleName) {
          await logRoleChange(
            formData.roleName,
            formData.roleName,
            'UPDATE',
            'role_name',
            selectedRole.name,
            formData.roleName
          );
        }
        // Log description change if it changed
        if (selectedRole.description !== formData.roleDescription) {
          await logRoleChange(
            formData.roleName,
            formData.roleName,
            'UPDATE',
            'description',
            selectedRole.description,
            formData.roleDescription
          );
        }
      } else {
        // Log role creation
        await logRoleChange(
          formData.roleName,
          formData.roleName,
          'CREATE',
          'role_created',
          null,
          formData.roleDescription
        );
      }

      setSuccessMessage(result.message || (isEditMode ? `Role "${formData.roleName}" updated successfully!` : `Role "${formData.roleName}" added successfully!`));
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowAddModal(false);
      setIsEditMode(false);
      resetForm();
      fetchRoles();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const endpoint = `/api/role_management/delete-role`;
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: selectedRole.name
        })
      });

      if (!response.ok) throw new Error('Failed to delete role');

      const result = await response.json();

      // Log role deletion
      await logRoleChange(
        selectedRole.name,
        selectedRole.name,
        'DELETE',
        'role_deleted',
        selectedRole.description,
        null
      );

      setSuccessMessage(result.message || 'Role deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowDeleteModal(false);
      setSelectedRole(null);
      fetchRoles();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      roleName: '',
      roleDescription: ''
    });
  };

  const filteredRoles = roles.filter(role =>
    role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.createdBy?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Loader2 style={{ width: '48px', height: '48px', color: 'var(--cares-blue)' }} className="animate-spin" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Page Header - Title removed to avoid duplication with Dashboard Header */}

      {/* Success Message */}
      {successMessage && (
        <div style={{
          background: 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))',
          border: '1px solid var(--cares-green)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Check style={{ width: '20px', height: '20px', color: 'var(--cares-green)' }} />
          <span style={{ color: 'var(--cares-green)', fontWeight: '500' }}>{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          background: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))',
          border: '1px solid var(--cares-orange)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <AlertCircle style={{ width: '20px', height: '20px', color: 'var(--cares-orange)' }} />
          <span style={{ color: 'var(--cares-orange)', fontWeight: '500' }}>{error}</span>
        </div>
      )}

      {/* Action Bar */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        {/* Search Bar */}
        <div style={{ position: 'relative', flex: '1', minWidth: '300px', maxWidth: '500px' }}>
          <Search style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: 'var(--cares-text-note)'
          }} />
          <input
            type="text"
            placeholder="Search roles by name, description, or creator..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 44px',
              border: '1px solid var(--cares-line-card)',
              borderRadius: '12px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = 'var(--cares-purple)'}
            onBlur={(e) => e.target.style.borderColor = 'var(--cares-line-card)'}
          />
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => {
              setIsEditMode(false);
              setShowAddModal(true);
              resetForm();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'var(--cares-green)',
              color: 'var(--cares-text-max)',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(16, 185, 129, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(16, 185, 129, 0.3)';
            }}
          >
            <Plus style={{ width: '18px', height: '18px' }} />
            Add Role
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'var(--cares-orange)',
              color: 'var(--cares-text-max)',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 6px rgba(239, 68, 68, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(239, 68, 68, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(239, 68, 68, 0.3)';
            }}
          >
            <Trash2 style={{ width: '18px', height: '18px' }} />
            Delete Role
          </button>
        </div>
      </div>

      {/* Roles Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '24px',
        marginBottom: '24px'
      }}>
        {filteredRoles.length > 0 ? (
          filteredRoles.map((role) => (
            <div
              key={role.id}
              style={{
                background: 'var(--cares-surface-card)',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                border: '1px solid var(--cares-line-card)',
                transition: 'all 0.2s',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
              }}
            >
              {/* Role Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Shield style={{ width: '24px', height: '24px', color: 'var(--cares-text-max)' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '18px',
                      fontWeight: '700',
                      color: 'var(--cares-text-1)',
                      marginBottom: '4px'
                    }}>
                      {role.name}
                    </h3>
                    <StatusBadge status={role.status} />
                  </div>
                </div>
              </div>

              {/* Role Description */}
              <p style={{
                margin: '0 0 16px 0',
                fontSize: '14px',
                color: 'var(--cares-text-label)',
                lineHeight: '1.5',
                minHeight: '42px'
              }}>
                {role.description || 'No description available'}
              </p>

              {/* Role Metadata */}
              <div style={{
                paddingTop: '16px',
                borderTop: '1px solid var(--cares-line-card)',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div>
                  <p style={{ margin: 0, fontSize: '11px', color: 'var(--cares-text-note)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Created By
                  </p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--cares-text-3)', fontWeight: '500' }}>
                    {role.createdBy}
                  </p>
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '11px', color: 'var(--cares-text-note)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Created Date
                  </p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--cares-text-3)', fontWeight: '500' }}>
                    {role.createdDate || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Edit Button */}
              <button
                onClick={() => {
                  setIsEditMode(true);
                  setSelectedRole(role);
                  setFormData({
                    roleName: role.name,
                    roleDescription: role.description
                  });
                  setShowAddModal(true);
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '10px',
                  background: 'var(--cares-blue)',
                  color: 'var(--cares-text-max)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-blue)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--cares-blue)'}
              >
                <Edit2 style={{ width: '14px', height: '14px' }} />
                Edit Role
              </button>
            </div>
          ))
        ) : (
          <div style={{
            gridColumn: '1 / -1',
            background: 'var(--cares-surface-card)',
            borderRadius: '16px',
            padding: '48px',
            textAlign: 'center',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <Shield style={{ width: '48px', height: '48px', color: 'var(--cares-text-note)', margin: '0 auto 16px' }} />
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-label)', marginBottom: '8px' }}>
              No roles found
            </h3>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--cares-text-note)' }}>
              {searchTerm ? 'No roles match your search criteria' : 'Get started by creating a new role'}
            </p>
          </div>
        )}
      </div>

      {/* Add Role Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '24px'
        }}>
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '20px',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Modal Header */}
            <div style={{
              background: isEditMode ? 'var(--cares-blue)' : 'var(--cares-green)',
              padding: '24px 32px',
              borderRadius: '20px 20px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {isEditMode ? (
                  <Edit2 style={{ width: '24px', height: '24px', color: 'var(--cares-text-max)' }} />
                ) : (
                  <Plus style={{ width: '24px', height: '24px', color: 'var(--cares-text-max)' }} />
                )}
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'var(--cares-text-max)' }}>
                  {isEditMode ? 'Edit Role' : 'Add New Role'}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setIsEditMode(false);
                  resetForm();
                }}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              >
                <X style={{ width: '20px', height: '20px', color: 'var(--cares-text-max)' }} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--cares-text-3)',
                  marginBottom: '8px'
                }}>
                  Role Name <span style={{ color: 'var(--cares-orange)' }}>*</span>
                </label>
                <input
                  type="text"
                  name="roleName"
                  value={formData.roleName}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Project Manager, Developer, Admin"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--cares-line-card)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--cares-purple)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--cares-line-card)'}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--cares-text-3)',
                  marginBottom: '8px'
                }}>
                  Role Description <span style={{ color: 'var(--cares-orange)' }}>*</span>
                </label>
                <textarea
                  name="roleDescription"
                  value={formData.roleDescription}
                  onChange={handleChange}
                  required
                  placeholder="Describe the responsibilities and permissions for this role..."
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--cares-line-card)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--cares-purple)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--cares-line-card)'}
                />
              </div>

              {/* Form Actions */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                paddingTop: '24px',
                borderTop: '1px solid var(--cares-line-card)'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  style={{
                    padding: '12px 24px',
                    background: 'var(--cares-surface-card)',
                    color: 'var(--cares-text-3)',
                    border: '1px solid var(--cares-line-card)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'var(--cares-surface-card)'}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '12px 24px',
                    background: isSubmitting ? 'var(--cares-surface-raised)' : (isEditMode ? 'var(--cares-blue)' : 'var(--cares-green)'),
                    color: 'var(--cares-text-max)',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isSubmitting && <Loader2 style={{ width: '16px', height: '16px' }} className="animate-spin" />}
                  {isSubmitting ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Role' : 'Add Role')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Role Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '24px'
        }}>
          <div style={{
            background: 'var(--cares-surface-card)',
            borderRadius: '20px',
            maxWidth: '900px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, var(--cares-orange) 0%, var(--cares-orange) 100%)',
              padding: '24px 32px',
              borderRadius: '20px 20px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Trash2 style={{ width: '24px', height: '24px', color: 'var(--cares-text-max)' }} />
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'var(--cares-text-max)' }}>
                  Delete Role
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedRole(null);
                }}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              >
                <X style={{ width: '20px', height: '20px', color: 'var(--cares-text-max)' }} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '32px', display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px' }}>
              {/* Role List */}
              <div>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: 'var(--cares-text-3)',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Select Role
                </h3>
                <div style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  border: '1px solid var(--cares-line-card)',
                  borderRadius: '12px',
                  padding: '8px'
                }}>
                  {roles.map((role) => (
                    <div
                      key={role.id}
                      onClick={() => setSelectedRole(role)}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        marginBottom: '4px',
                        background: selectedRole?.id === role.id ? 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))' : 'var(--cares-surface-card)',
                        border: selectedRole?.id === role.id ? '2px solid var(--cares-blue)' : '2px solid transparent',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedRole?.id !== role.id) {
                          e.currentTarget.style.background = 'var(--cares-surface-board)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedRole?.id !== role.id) {
                          e.currentTarget.style.background = 'var(--cares-surface-card)';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Shield style={{ width: '16px', height: '16px', color: 'var(--cares-purple)' }} />
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-1)' }}>
                          {role.name}
                        </p>
                      </div>
                      <p style={{ margin: '4px 0 0 24px', fontSize: '12px', color: 'var(--cares-text-label)', lineHeight: '1.4' }}>
                        {role.description?.substring(0, 50)}{role.description?.length > 50 ? '...' : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Role Details */}
              <div>
                {selectedRole ? (
                  <div>
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: '700',
                      color: 'var(--cares-text-3)',
                      marginBottom: '16px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Confirm Deletion
                    </h3>
                    <div style={{
                      background: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))',
                      border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))',
                      borderRadius: '12px',
                      padding: '20px',
                      marginBottom: '24px'
                    }}>
                      <p style={{
                        margin: 0,
                        fontSize: '14px',
                        color: 'var(--cares-orange)',
                        fontWeight: '500'
                      }}>
                        ⚠️ Warning: This action cannot be undone. Deleting this role may affect users who are currently assigned to it.
                      </p>
                    </div>

                    <div style={{
                      background: 'var(--cares-surface-board)',
                      borderRadius: '12px',
                      padding: '20px',
                      marginBottom: '24px'
                    }}>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Shield style={{ width: '24px', height: '24px', color: 'var(--cares-text-max)' }} />
                          </div>
                          <div>
                            <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--cares-text-1)' }}>
                              {selectedRole.name}
                            </h4>
                            <StatusBadge status={selectedRole.status} />
                          </div>
                        </div>
                        <p style={{ margin: 0, fontSize: '14px', color: 'var(--cares-text-label)', lineHeight: '1.5' }}>
                          {selectedRole.description}
                        </p>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', paddingTop: '16px', borderTop: '1px solid var(--cares-line-card)' }}>
                        <div>
                          <p style={{ margin: 0, fontSize: '12px', color: 'var(--cares-text-label)', fontWeight: '600' }}>
                            Created By
                          </p>
                          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'var(--cares-text-1)', fontWeight: '500' }}>
                            {selectedRole.createdBy}
                          </p>
                        </div>
                        <div>
                          <p style={{ margin: 0, fontSize: '12px', color: 'var(--cares-text-label)', fontWeight: '600' }}>
                            Created Date
                          </p>
                          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'var(--cares-text-1)', fontWeight: '500' }}>
                            {selectedRole.createdDate || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                      <button
                        onClick={() => setSelectedRole(null)}
                        style={{
                          padding: '12px 24px',
                          background: 'var(--cares-surface-card)',
                          color: 'var(--cares-text-3)',
                          border: '1px solid var(--cares-line-card)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cares-surface-board)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'var(--cares-surface-card)'}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDelete}
                        disabled={isSubmitting}
                        style={{
                          padding: '12px 24px',
                          background: isSubmitting ? 'var(--cares-surface-raised)' : 'linear-gradient(135deg, var(--cares-orange) 0%, var(--cares-orange) 100%)',
                          color: 'var(--cares-text-max)',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        {isSubmitting && <Loader2 style={{ width: '16px', height: '16px' }} className="animate-spin" />}
                        {isSubmitting ? 'Deleting...' : 'Confirm Delete'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    minHeight: '300px',
                    color: 'var(--cares-text-note)'
                  }}>
                    <Shield style={{ width: '48px', height: '48px', marginBottom: '16px', opacity: 0.5 }} />
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--cares-text-label)' }}>
                      No role selected
                    </h3>
                    <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                      Select a role from the list to delete
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;

